<script setup lang="ts">
import { computed, ref, watch } from "vue";
import type { ItemDTO } from "@barkup/shared";
import { fetchItemThumbUrl } from "../api.js";
import { fileIconFor } from "../fileIcon.js";
import { itemTitle } from "../format.js";
import { useLongPress } from "../composables/useLongPress.js";
import { highlightSegments } from "../search.js";

/**
 * Square grid tile (the photos-first view). Images fill the tile with their
 * thumbnail; other kinds show the kind icon with a filename strip. Same
 * gesture vocabulary as ItemCard: tap opens, long-press selects — per-tile
 * actions live in the detail view / selection bar, not on the tile.
 */
const props = defineProps<{
  item: ItemDTO;
  selected: boolean;
  selectionMode: boolean;
  /** Active search query — emphasises the matched run in the name strip. See ItemCard for why it's weight-only. */
  highlight?: string;
}>();

const emit = defineEmits<{
  open: [id: string];
  "toggle-select": [id: string];
  "enter-select": [id: string];
}>();

const kindMeta = computed(
  () =>
    ({
      FILE: { icon: fileIconFor(props.item), tile: "bg-primary-container text-primary" },
      IMAGE: { icon: "image", tile: "bg-secondary-container text-secondary" },
      VIDEO: { icon: "movie", tile: "bg-surface-container-high text-on-surface-variant" },
      LINK: { icon: "link", tile: "bg-tertiary-container text-tertiary" },
    })[props.item.kind]
);

const title = computed(() => itemTitle(props.item));
const showNameStrip = computed(() => props.item.kind !== "IMAGE" || !thumbUrl.value);
const titleSegments = computed(() => highlightSegments(title.value, props.highlight ?? ""));
/** See ItemCard: recede only when something in this string is actually emphasised. */
const titleHasHit = computed(() => titleSegments.value.some((seg) => seg.hit));

const thumbUrl = ref<string | null>(null);
watch(
  () => props.item.id,
  (itemId) => {
    thumbUrl.value = null;
    if (props.item.kind !== "IMAGE" || !props.item.hasThumb) return;
    fetchItemThumbUrl(itemId)
      .then((url) => {
        if (props.item.id === itemId) thumbUrl.value = url;
      })
      .catch(() => void 0);
  },
  { immediate: true }
);

const { onPointerdown, onPointermove, onPointerup, consumeLongPress } = useLongPress(() =>
  emit("enter-select", props.item.id)
);

function onTileClick(): void {
  if (consumeLongPress()) return;
  if (props.selectionMode) emit("toggle-select", props.item.id);
  else emit("open", props.item.id);
}
</script>

<template>
  <div
    class="relative aspect-square select-none overflow-hidden rounded-xl transition-all duration-150"
    :class="[kindMeta.tile, selected ? 'ring-2 ring-primary' : '']"
    role="button"
    tabindex="0"
    :aria-label="title"
    :aria-pressed="selectionMode ? selected : undefined"
    @pointerdown="onPointerdown"
    @pointermove="onPointermove"
    @pointerup="onPointerup"
    @pointercancel="onPointerup"
    @click="onTileClick"
    @keydown.enter="onTileClick"
    @contextmenu.prevent
  >
    <img v-if="thumbUrl" :src="thumbUrl" :alt="title" class="h-full w-full object-cover" draggable="false" />
    <span v-else class="flex h-full w-full items-center justify-center">
      <span class="material-symbols-outlined text-[32px]">{{ kindMeta.icon }}</span>
    </span>

    <!-- Filename strip for non-photo tiles — tonal, no gradient (design-system rule). -->
    <span
      v-if="showNameStrip"
      class="absolute inset-x-0 bottom-0 truncate bg-surface-container-lowest/90 px-2 py-1 text-center font-metadata"
      :class="titleHasHit ? 'text-on-surface-variant' : 'text-on-surface'"
      ><template v-for="(seg, i) in titleSegments" :key="i"
        ><span v-if="seg.hit" class="font-semibold text-on-surface">{{ seg.text }}</span
        ><template v-else>{{ seg.text }}</template></template
      ></span
    >

    <!-- Selection indicator: check bubble, top-left (Photos convention). -->
    <span
      v-if="selectionMode"
      class="absolute left-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full border transition-colors duration-150"
      :class="selected ? 'border-primary bg-primary text-on-primary' : 'border-outline bg-surface-container-lowest/80'"
      aria-hidden="true"
    >
      <span v-if="selected" class="material-symbols-outlined text-[16px]">check</span>
    </span>
  </div>
</template>
